// Frontend Sentry integration (issue #110).
//
// Why this is not `@sentry/react`: adding the SDK as an npm dependency would
// bundle ~80–100 KB into every page load, even for visitors who reject the
// analytics consent. Instead we load the Sentry browser SDK from the public
// CDN (`browser.sentry-cdn.com`) on demand — and only AFTER the user has
// granted analytics consent through `useCookieConsent`. The bundle is fetched
// once, cached by the browser, and configured via `Sentry.init(...)` to mirror
// the `@sentry/react` defaults (unhandled errors, navigation breadcrumbs,
// fetch/console breadcrumbs).
//
// This module exposes a tiny façade:
//   - `initSentry({ dsn, environment, release })` — idempotent; resolves
//     `true` when Sentry is wired up and `false` if it is skipped (no DSN,
//     no consent, SSR, or load failure).
//   - `captureException(err, ctx)` — best-effort capture; no-ops when
//     Sentry has not been initialised.
//   - `addBreadcrumb(crumb)` — convenience pass-through.
//
// The default loader URL is overridable so tests can inject a mock that
// resolves to a fake `window.Sentry` synchronously without touching the
// network.

const DEFAULT_SDK_URL = "https://browser.sentry-cdn.com/8.40.0/bundle.tracing.min.js";
const SDK_SCRIPT_ID = "pikra-sentry-sdk";

let initState = "idle"; // "idle" | "loading" | "ready" | "failed"
let pendingInit = null;

function readBrowserSentry() {
  if (typeof window === "undefined") return null;
  const s = window.Sentry;
  if (!s || typeof s.init !== "function") return null;
  return s;
}

function loadSentryScript(src) {
  return new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("no document"));
      return;
    }
    const existing = document.getElementById(SDK_SCRIPT_ID);
    if (existing) {
      // Another caller is already loading the SDK. Wait on its load event.
      existing.addEventListener("load", () => {
        const ready = readBrowserSentry();
        ready ? resolve(ready) : reject(new Error("Sentry not on window"));
      });
      existing.addEventListener("error", () => reject(new Error("Sentry script error")));
      return;
    }
    const script = document.createElement("script");
    script.id = SDK_SCRIPT_ID;
    script.src = src;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.onload = () => {
      const ready = readBrowserSentry();
      ready ? resolve(ready) : reject(new Error("Sentry not on window"));
    };
    script.onerror = () => reject(new Error("Sentry script error"));
    document.head.appendChild(script);
  });
}

export async function initSentry(options = {}) {
  if (initState === "ready") return true;
  if (initState === "failed") return false;
  if (pendingInit) return pendingInit;

  const dsn = options.dsn;
  if (!dsn) return false;
  if (typeof window === "undefined") return false;

  initState = "loading";
  pendingInit = (async () => {
    try {
      let sentry = readBrowserSentry();
      if (!sentry) {
        sentry = await loadSentryScript(options.sdkUrl || DEFAULT_SDK_URL);
      }
      sentry.init({
        dsn,
        environment: options.environment || "production",
        release: options.release,
        // Capture-all defaults that mirror @sentry/react's auto-instrumentation
        // (unhandled errors + unhandledrejection + history/fetch breadcrumbs).
        // Tracing is sampled lightly so it does not balloon the org quota.
        tracesSampleRate: options.tracesSampleRate ?? 0.1,
        sendDefaultPii: false,
        autoSessionTracking: true,
      });
      initState = "ready";
      return true;
    } catch (err) {
      initState = "failed";
      // Surfacing as console.warn (not console.error) keeps observability
      // failures from looking like product errors in dev tools.
      if (typeof console !== "undefined" && typeof console.warn === "function") {
        console.warn("[sentry] init skipped:", err && err.message);
      }
      return false;
    } finally {
      pendingInit = null;
    }
  })();
  return pendingInit;
}

export function captureException(error, context) {
  const sentry = readBrowserSentry();
  if (initState !== "ready" || !sentry) return false;
  try {
    sentry.captureException(error, context);
    return true;
  } catch {
    return false;
  }
}

export function addBreadcrumb(crumb) {
  const sentry = readBrowserSentry();
  if (initState !== "ready" || !sentry) return false;
  try {
    sentry.addBreadcrumb(crumb);
    return true;
  } catch {
    return false;
  }
}

export function isSentryReady() {
  return initState === "ready";
}

// Test-only: reset module-level state between tests.
export function __resetSentryForTests() {
  initState = "idle";
  pendingInit = null;
  if (typeof document !== "undefined") {
    const existing = document.getElementById(SDK_SCRIPT_ID);
    if (existing) existing.remove();
  }
}
